// Top 10 players as floating islands in the sky round the village (home view, online only). Each island carries that
// player's Sol Hall at their level among a few pines, and bobs gently; tapping one visits their village
// (input.js → isleAt → visitBase). Each island is painted once into its own canvas (per player, rank, size) and blitted.
// The sky itself (sky.js) leaves out its decorative islands while these are shown.
import { N } from '../config.js';
import { W, H, DPR, ox, oy, tw, th, zs, ISL, withView, ctx } from './view.js';
import { DRAW } from './buildings.js';
import { dTree0 } from './ground.js';
import { hash } from './primitives.js';
import { floatIsle } from './sky.js';
import { topTen, uid } from '../online.js';

const CORE_SIZE = 3;
// spots for pines round the Sol Hall in its world units (footprint 0..3), all on the island's grass and none in front of
// the hall's face: behind, at the sides, front corners. Each island takes 2-5 of them, picked from the player's id.
const PINE_SPOTS = [[-.8, 1], [1, -.8], [-1.4, 1.2], [1.2, -1.4], [-1.2, 2.6], [2.6, -1.2], [.4, 3.8], [3.8, .4]];
function pinesFor(id) { let h = 7; for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const pick = PINE_SPOTS.map((p, i) => ({ p, r: hash(i, h) })).sort((a, b) => a.r - b.r).slice(0, 2 + (Math.abs(h) % 4));
  return pick.map(({ p }, i) => ({ x: p[0] + (hash(i, h + 1) - .5) * .4, y: p[1] + (hash(i, h + 2) - .5) * .4, s: 1 + hash(i, h + 3) * .45, seed: h + i })); }
// slots as screen fractions, rank 1 first: two columns down the sides (landscape) or the corners (portrait),
// clear of the top bar and the Build/Attack buttons
const SLOTS_WIDE = [[.07, .3], [.93, .3], [.13, .45], [.87, .45], [.06, .6], [.94, .6], [.12, .75], [.88, .75], [.2, .2], [.8, .2]];
const SLOTS_TALL = [[.13, .24], [.87, .24], [.13, .37], [.87, .37], [.13, .66], [.87, .66], [.13, .79], [.87, .79], [.37, .17], [.63, .17]];
let isles = [], isleKey = '', hidden = null;
const isleSize = () => Math.max(20, Math.min(46, Math.min(W, H) * .055));

function paintIsle(p, rank, s) {
  const lvl =Math.max(1, Math.min(17, p.core | 0 || 1)), t = s * 1.25 / CORE_SIZE;
  const cw = s * 3.6, top = s * 1.2 + CORE_SIZE * 1.25 * t + 14, chH = top + s * 2.3 + 22;
  const c = document.createElement('canvas'); c.width = Math.ceil(cw * DPR); c.height = Math.ceil(chH * DPR);
  const g = c.getContext('2d'); g.setTransform(DPR, 0, 0, DPR, 0, 0);
  const cx = cw / 2, cy = top;
  floatIsle(g, cx, cy, s, rank * 7 + 3, true);
  // the Sol Hall (footprint centred on the island's grass top) with pines round it, back to front
  withView({ ctx: g, ox: cx, oy: cy - CORE_SIZE * t / 4 - s * .04, tw: t }, () => {
    const b = { id: 7, type: 'core', x: 0, y: 0, lvl, stored: 0, last: Date.now(), hp: 1, maxHp: 1 };
    const pines = pinesFor(p.id), pine = q => dTree0({ ...q, k: 'tree', v: .2 }), behind = q => q.x + q.y < CORE_SIZE;
    ctx.fillStyle = 'rgba(10,20,5,.25)'; ctx.beginPath(); ctx.ellipse(cx, cy, s * .7, s * .24, 0, 0, 7); ctx.fill();
    pines.filter(behind).forEach(pine); DRAW.core(b, 1200, false); pines.filter(q => !behind(q)).forEach(pine);
  });
  return { c, cw, chH, cx, cy, s, p };
}

/** Rebuilds the islands when the Top 10 or the screen changes. skip: the player whose village is being visited (their island
 *  is left out, the others keep their places). Returns whether any are shown (the sky drops its own then). */
function syncIsles(skip = null) {
  const list = topTen(), s = isleSize();
  const key = list.length ? [W, H, DPR, uid, ...list.map(p => [p.id, p.core | 0, p.name].join(':'))].join('|') : '';
  if (key !== isleKey) {
    isleKey = key; const slots = W >= H ? SLOTS_WIDE : SLOTS_TALL;
    isles = list.map((p, i) => ({ ...paintIsle(p, i, s * (i === 0 ? 1.2 : i < 3 ? 1.1 : 1)), x: slots[i][0] * W, y: slots[i][1] * H, i }));
  }
  hidden = skip; return isles.some(it => it.p.id !== skip);
}
const bob = (it, now) => Math.sin(now / 1700 + it.i * 1.9) * it.s * .08;
/** Draws the islands (after the sky, before the village island, which covers any it overlaps). */
function drawTopIsles(c, now) {
  for (const it of isles) if (it.p.id !== hidden) c.drawImage(it.c, it.x - it.cx, it.y - it.cy + bob(it, now), it.cw, it.chH);
}
// Is screen point (sx, sy) over the village island (its top or the cliff under its front edges)?
function overVillage(sx, sy) {
  const inside = y => { const a = (sx - ox) / (tw / 2), b = (y - oy) / (th / 2), x = (a + b) / 2, z = (b - a) / 2, m = ISL + .3; return x > -m && z > -m && x < N + m && z < N + m; };
  return inside(sy) || inside(sy - 1.3 * zs);
}
/** The Top 10 player whose island is under screen point (sx, sy), or null. */
function isleAt(sx, sy, now = performance.now()) {
  if (!isles.length || overVillage(sx, sy)) return null;
  for (const it of isles) { if (it.p.id === hidden) continue; const y = it.y + bob(it, now), s = it.s;
    if (Math.abs(sx - it.x) < s * 1.3 && sy > y - s * 2.2 && sy < y + s * 1.9) return it.p; }
  return null;
}
export { syncIsles, drawTopIsles, isleAt };
